import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { toast } from "sonner";
import {
  Send, RefreshCw, X, Plus, Trash2, Copy, FileDown, Calendar, ShieldCheck,
} from "lucide-react";
import { fmt } from "./admin/_helpers";
import OverviewTab from "./admin/OverviewTab";
import UsersTab from "./admin/UsersTab";
import SubscribersTab from "./admin/SubscribersTab";
import ContactsTab from "./admin/ContactsTab";
import EmailHealthTab from "./admin/EmailHealthTab";
import EpisodesTab from "./admin/EpisodesTab";
import FeatureUsageTab from "./admin/FeatureUsageTab";
import PreflightDialog from "./admin/PreflightDialog";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "feature-usage", label: "Feature usage" },
  { id: "users", label: "Users" },
  { id: "subscribers", label: "Newsletter" },
  { id: "compose", label: "Compose newsletter" },
  { id: "episodes", label: "Podcast episodes" },
  { id: "contacts", label: "Messages" },
  { id: "email-health", label: "Email health" },
];

export default function AdminPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [usersQ, setUsersQ] = useState("");
  const [subs, setSubs] = useState([]);
  const [subsFilter, setSubsFilter] = useState("active");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState({
    subject: "", month_year: "", headline: "", subhead: "", preheader: "",
    hero_image_url: "",
    intro_paragraph: "", intro_paragraph_2: "",
    article_section_label: "", article_title: "", article_body: "", article_cta_label: "", article_cta_url: "",
    podcast_episode_title: "", podcast_meta: "", podcast_teaser: "", podcast_url: "",
    gang_title: "", gang_body: "",
    upcoming_events: ["", "", ""],
    closing_line: "",
    substack_url: "https://dogsandhorses.substack.com",
    scheduled_at: "",
  });
  const [previewHtml, setPreviewHtml] = useState("");
  const [sending, setSending] = useState(false);
  // Scheduled sends — pending queue and the toggle for "schedule for later".
  const [scheduledSends, setScheduledSends] = useState([]);
  const [scheduleMode, setScheduleMode] = useState(false);
  // Preflight modal state — runs auth/domain/sender/subscriber checks
  // against Resend and surfaces the results before the admin hits send.
  const [preflightOpen, setPreflightOpen] = useState(false);
  const [preflightBusy, setPreflightBusy] = useState(false);
  const [preflightResult, setPreflightResult] = useState(null);

  const runPreflight = async () => {
    setPreflightOpen(true);
    setPreflightBusy(true);
    setPreflightResult(null);
    try {
      const { data } = await api.post("/admin/newsletter/preflight");
      setPreflightResult(data);
    } catch (err) {
      setPreflightResult({
        ok: false,
        checks: [{ name: "Request", status: "fail", detail: err?.response?.data?.detail || err?.message || "Preflight request failed" }],
      });
    } finally {
      setPreflightBusy(false);
    }
  };
  const [broadcasts, setBroadcasts] = useState([]);
  // Drafts: list + which draft (if any) the user has loaded so we can offer
  // "Update saved draft" vs "Save as new draft".
  const [drafts, setDrafts] = useState([]);
  const [loadedDraftId, setLoadedDraftId] = useState("");
  const [savingDraft, setSavingDraft] = useState(false);
  // Auto-save: timestamp of the last successful auto-save + a "tick" to drive
  // the "Saved Xs ago" indicator. Refs hold the debounce timer and the latest
  // compose snapshot so the debounce closure always saves the freshest content.
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [now, setNow] = useState(Date.now());
  const autoSaveTimer = useRef(null);
  const latestComposeRef = useRef(null);
  const loadedDraftIdRef = useRef("");

  useEffect(() => {
    if (!isAdmin) return;
    setLoading(true);
    api.get("/admin/stats")
      .then(({ data }) => setStats(data))
      .catch((err) => { console.warn("admin stats failed:", err); })
      .finally(() => setLoading(false));
  }, [isAdmin]);

  const loadUsers = async (q = "") => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: q ? { q } : {} });
      setUsers(data.items || []);
    } finally { setLoading(false); }
  };

  const loadSubs = async (status) => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/subscribers", { params: status === "all" ? {} : { status } });
      setSubs(data.items || []);
    } finally { setLoading(false); }
  };

  const loadContacts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contacts");
      setContacts(data.items || []);
    } finally { setLoading(false); }
  };

  const loadBroadcasts = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/history");
      setBroadcasts(data.items || []);
    } catch (err) {
      console.debug("loadBroadcasts: could not fetch newsletter history", err);
    }
  };

  // Email health (driven by db.email_events from the Resend webhook)
  const [emailHealth, setEmailHealth] = useState(null);
  const [recentOptouts, setRecentOptouts] = useState([]);

  const loadEmailHealth = async () => {
    try {
      const [health, opts] = await Promise.all([
        api.get("/admin/email/health"),
        api.get("/admin/email/recent-optouts", { params: { limit: 20 } }),
      ]);
      setEmailHealth(health.data);
      setRecentOptouts(opts.data.items || []);
    } catch (err) {
      console.debug("loadEmailHealth: fetch failed", err);
    }
  };

  const loadDrafts = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/drafts");
      setDrafts(data.items || []);
    } catch (err) {
      console.debug("loadDrafts: could not fetch drafts", err);
    }
  };

  const loadScheduled = async () => {
    try {
      const { data } = await api.get("/admin/newsletter/scheduled");
      setScheduledSends(data.items || []);
    } catch (err) {
      console.debug("loadScheduled: could not fetch scheduled sends", err);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;
    if (tab === "users") loadUsers(usersQ);
    if (tab === "subscribers") loadSubs(subsFilter);
    if (tab === "contacts") loadContacts();
    if (tab === "email-health") loadEmailHealth();
    if (tab === "compose") { loadBroadcasts(); loadDrafts(); loadScheduled(); }
  }, [tab, isAdmin]);

  // ---- Auto-save ----
  //
  // Behaviour: once the admin has loaded OR saved a draft for the first time
  // (loadedDraftId is set), every subsequent edit to the compose form is
  // debounced and PUT to /admin/newsletter/drafts/{id} 2 seconds after the
  // last keystroke. Failures are silent (logged to console) so they don't
  // pop a toast while the user is typing — the next successful tick will
  // catch up. A "Saved Xs ago" indicator re-renders every 5 seconds.
  useEffect(() => {
    latestComposeRef.current = compose;
    loadedDraftIdRef.current = loadedDraftId;

    if (!loadedDraftId) return undefined; // nothing to save until first manual save
    if (tab !== "compose") return undefined;

    if (autoSaveTimer.current) window.clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = window.setTimeout(async () => {
      const draftId = loadedDraftIdRef.current;
      const payload = latestComposeRef.current;
      if (!draftId || !payload) return;
      const existing = drafts.find((x) => x.id === draftId);
      const name = (existing?.name) || (payload.subject || "").trim() || "Untitled draft";
      setAutoSaving(true);
      try {
        await api.put(`/admin/newsletter/drafts/${draftId}`, { ...payload, name });
        setLastSavedAt(Date.now());
      } catch (err) {
        // Silent — let the user keep typing. Surface only in console.
        console.debug("autosave: PUT failed", err);
      } finally {
        setAutoSaving(false);
      }
    }, 2000);

    return () => {
      if (autoSaveTimer.current) {
        window.clearTimeout(autoSaveTimer.current);
        autoSaveTimer.current = null;
      }
    };
  }, [compose, loadedDraftId, tab, drafts]);

  // Tick every 5s so "Saved Xs ago" stays fresh without re-rendering on every
  // keystroke. Stops when there's nothing to display.
  useEffect(() => {
    if (!lastSavedAt) return undefined;
    const t = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(t);
  }, [lastSavedAt]);

  // Block non-admins (rendered AFTER hooks, so hook order is stable)
  if (user && user.id && !isAdmin) {
    return <Navigate to="/welcome" replace />;
  }

  const markRead = async (id) => {
    await api.post(`/admin/contacts/${id}/read`);
    loadContacts();
  };

  const deleteUser = async (u) => {
    const confirmed = window.confirm(
      `Permanently delete ${u.email}?\n\nThis will wipe all their dogs, events, vet records, weights, ` +
      `journal entries, show plans, show results, lessons, reminders, shares, and the user account itself.\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    try {
      const { data } = await api.delete(`/admin/users/${u.id}`);
      toast.success(`Deleted ${data.deleted_email}`);
      loadUsers();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const setC = (k, v) => setCompose((cur) => ({ ...cur, [k]: v }));
  const setEvent = (i, v) => setCompose((cur) => {
    const next = [...cur.upcoming_events];
    next[i] = v;
    return { ...cur, upcoming_events: next };
  });
  const addEventRow = () => setCompose((cur) => ({ ...cur, upcoming_events: [...cur.upcoming_events, ""] }));
  const removeEventRow = (i) => setCompose((cur) => {
    const next = [...cur.upcoming_events];
    next.splice(i, 1);
    return { ...cur, upcoming_events: next.length ? next : [""] };
  });

  // ---- Drafts ----
  const emptyCompose = {
    subject: "", month_year: "", headline: "", subhead: "", preheader: "",
    hero_image_url: "",
    intro_paragraph: "", intro_paragraph_2: "",
    article_section_label: "", article_title: "", article_body: "", article_cta_label: "", article_cta_url: "",
    podcast_episode_title: "", podcast_meta: "", podcast_teaser: "", podcast_url: "",
    gang_title: "", gang_body: "",
    upcoming_events: ["", "", ""],
    closing_line: "",
    substack_url: "https://dogsandhorses.substack.com",
    scheduled_at: "",
  };

  const newBlankDraft = () => {
    setCompose(emptyCompose);
    setLoadedDraftId("");
    setPreviewHtml("");
  };

  const loadDraft = (draftId) => {
    if (!draftId) { setLoadedDraftId(""); return; }
    const d = drafts.find((x) => x.id === draftId);
    if (!d) return;
    const payload = d.payload || {};
    // Merge over the empty template so any new fields added later default cleanly.
    setCompose({ ...emptyCompose, ...payload });
    setLoadedDraftId(d.id);
    setPreviewHtml("");
    toast.success(`Loaded draft "${d.name}"`);
  };

  const saveDraftAsNew = async () => {
    const suggested = (compose.subject || "").trim() || "Untitled draft";
    const name = window.prompt("Name this draft:", suggested);
    if (name == null) return; // user cancelled
    setSavingDraft(true);
    try {
      const { data } = await api.post("/admin/newsletter/drafts", { ...compose, name: name.trim() || suggested });
      toast.success(`Draft "${data.name}" saved`);
      setLoadedDraftId(data.id);
      await loadDrafts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const updateLoadedDraft = async () => {
    if (!loadedDraftId) return;
    setSavingDraft(true);
    try {
      const existing = drafts.find((x) => x.id === loadedDraftId);
      const name = (existing?.name) || (compose.subject || "").trim() || "Untitled draft";
      const { data } = await api.put(`/admin/newsletter/drafts/${loadedDraftId}`, { ...compose, name });
      toast.success(`Updated draft "${data.name}"`);
      await loadDrafts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update draft");
    } finally {
      setSavingDraft(false);
    }
  };

  const deleteLoadedDraft = async () => {
    if (!loadedDraftId) return;
    const existing = drafts.find((x) => x.id === loadedDraftId);
    const confirmed = window.confirm(`Delete draft "${existing?.name || "Untitled"}"? This can't be undone.`);
    if (!confirmed) return;
    try {
      await api.delete(`/admin/newsletter/drafts/${loadedDraftId}`);
      toast.success("Draft deleted");
      setLoadedDraftId("");
      await loadDrafts();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete draft");
    }
  };

  const renderSavedHint = () => {
    if (autoSaving) return "Saving…";
    if (!lastSavedAt) return "";
    const ageSec = Math.max(0, Math.floor((now - lastSavedAt) / 1000));
    if (ageSec < 5) return "Saved just now";
    if (ageSec < 60) return `Saved ${ageSec}s ago`;
    if (ageSec < 3600) return `Saved ${Math.floor(ageSec / 60)}m ago`;
    return `Saved ${Math.floor(ageSec / 3600)}h ago`;
  };


  const refreshPreview = async () => {
    try {
      const { data } = await api.post("/admin/newsletter/preview", compose);
      setPreviewHtml(data.html || "");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    }
  };

  // Substack accepts pasted Markdown well, so we offer a one-click copy + an
  // optional .md download. Both call the same backend renderer; the download
  // also gets YAML front-matter so static-blog importers can read the title
  // and date without manual entry.
  const fetchMarkdown = async (withFrontmatter = false) => {
    const { data } = await api.post(
      `/admin/newsletter/markdown${withFrontmatter ? "?frontmatter=1" : ""}`,
      compose,
    );
    return data.markdown || "";
  };

  const copyMarkdownForSubstack = async () => {
    try {
      const md = await fetchMarkdown(false);
      if (!md.trim()) {
        toast.error("Nothing to copy yet — fill in the form first.");
        return;
      }
      await navigator.clipboard.writeText(md);
      toast.success("Markdown copied — paste it into Substack");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Copy failed");
    }
  };

  const downloadMarkdownFile = async () => {
    try {
      const md = await fetchMarkdown(true);
      if (!md.trim()) {
        toast.error("Nothing to download yet — fill in the form first.");
        return;
      }
      const slug = (compose.headline || compose.subject || "newsletter")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "")
        .slice(0, 60) || "newsletter";
      const blob = new Blob([md], { type: "text/markdown;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${slug}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${slug}.md`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Download failed");
    }
  };

  const sendNewsletter = async (test_only) => {
    if (!compose.subject || !compose.headline) {
      toast.error("Subject and headline are required.");
      return;
    }

    // Build the request payload — for a scheduled broadcast we convert the
    // datetime-local string to a UTC ISO timestamp so the backend can compare
    // it deterministically regardless of the admin's timezone.
    let scheduledIso = "";
    if (!test_only && scheduleMode) {
      if (!compose.scheduled_at) {
        toast.error("Pick a date & time to schedule.");
        return;
      }
      const localDate = new Date(compose.scheduled_at);
      if (Number.isNaN(localDate.getTime())) {
        toast.error("Couldn't parse that schedule time.");
        return;
      }
      if (localDate.getTime() <= Date.now() + 30_000) {
        toast.error("Pick a time at least a minute in the future.");
        return;
      }
      scheduledIso = localDate.toISOString();
    }
    const payload = { ...compose, test_only, scheduled_at: scheduledIso };

    const label = test_only
      ? "yourself"
      : scheduledIso
        ? `all active subscribers on ${new Date(scheduledIso).toLocaleString()}`
        : "all active subscribers";
    const confirmed = window.confirm(`${scheduledIso && !test_only ? "Schedule" : "Send"} newsletter to ${label}?`);
    if (!confirmed) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/send", payload);
      if (data.scheduled) {
        toast.success(`Scheduled for ${new Date(data.scheduled_at).toLocaleString()}`);
        setScheduleMode(false);
        setC("scheduled_at", "");
        loadScheduled();
      } else if (test_only) {
        toast.success(`Test sent to your inbox (${data.sent} delivered, ${data.failed} failed)`);
      } else {
        toast.success(`Newsletter sent — ${data.sent} delivered, ${data.failed} failed`);
        loadBroadcasts();
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (id) => {
    if (!window.confirm("Cancel this scheduled send?")) return;
    try {
      await api.delete(`/admin/newsletter/scheduled/${id}`);
      toast.success("Cancelled");
      loadScheduled();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <SectionTitle overline="Admin" title="Admin dashboard" />

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-border" data-testid="admin-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`admin-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab stats={stats} />}

      {tab === "feature-usage" && <FeatureUsageTab />}

      {tab === "users" && (
        <UsersTab
          users={users}
          usersQ={usersQ}
          setUsersQ={setUsersQ}
          loadUsers={loadUsers}
          loading={loading}
          deleteUser={deleteUser}
          currentUserEmail={user?.email}
        />
      )}

      {tab === "subscribers" && (
        <SubscribersTab
          subs={subs}
          subsFilter={subsFilter}
          setSubsFilter={setSubsFilter}
          loadSubs={loadSubs}
          loading={loading}
        />
      )}

      {tab === "compose" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="admin-compose">
          {/* ====== LEFT: form ====== */}
          <div className="space-y-5">
            <Card className="border-border shadow-none rounded-lg" data-testid="drafts-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <h3 className="font-medium">Drafts</h3>
                    {loadedDraftId && (
                      <span
                        className="text-[11px] text-muted-foreground tabular-nums truncate"
                        data-testid="drafts-saved-indicator"
                      >
                        {renderSavedHint()}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={newBlankDraft}
                    className="text-xs text-muted-foreground hover:text-foreground underline"
                    data-testid="drafts-new-blank"
                  >
                    Start blank
                  </button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    value={loadedDraftId}
                    onChange={(e) => loadDraft(e.target.value)}
                    className="flex-1 min-w-[160px] h-9 border border-border rounded-md bg-card px-3 text-sm"
                    data-testid="drafts-select"
                  >
                    <option value="">— Load a saved draft —</option>
                    {drafts.map((d) => (
                      <option key={d.id} value={d.id}>{d.name}</option>
                    ))}
                  </select>
                  {loadedDraftId ? (
                    <>
                      <Button
                        onClick={updateLoadedDraft}
                        disabled={savingDraft}
                        className="bg-primary text-primary-foreground hover:bg-primary/90"
                        data-testid="drafts-update"
                      >
                        {savingDraft ? "Saving…" : "Update draft"}
                      </Button>
                      <Button
                        onClick={saveDraftAsNew}
                        disabled={savingDraft}
                        variant="outline"
                        data-testid="drafts-save-as"
                      >
                        Save as new…
                      </Button>
                      <button
                        onClick={deleteLoadedDraft}
                        className="text-xs text-destructive hover:underline"
                        data-testid="drafts-delete"
                      >
                        Delete
                      </button>
                    </>
                  ) : (
                    <Button
                      onClick={saveDraftAsNew}
                      disabled={savingDraft}
                      className="bg-primary text-primary-foreground hover:bg-primary/90"
                      data-testid="drafts-save-new"
                    >
                      {savingDraft ? "Saving…" : "Save current as draft"}
                    </Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground" data-testid="drafts-help">
                  Drafts let you save in-progress newsletters without sending. They&apos;re private to your admin email and load straight back into this form. Once a draft is saved or loaded, edits <strong>auto-save every 2 seconds</strong>.
                </p>
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-medium">Header</h3>
                <div>
                  <Label>Subject line (inbox)</Label>
                  <Input value={compose.subject} onChange={(e) => setC("subject", e.target.value)} placeholder="Your Dog Days · February — Show season warm-up" data-testid="compose-subject" />
                </div>
                <div>
                  <Label>Preheader (inbox preview)</Label>
                  <Input value={compose.preheader} onChange={(e) => setC("preheader", e.target.value)} placeholder="Grooming tips + new podcast drop" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Month & year</Label>
                    <Input value={compose.month_year} onChange={(e) => setC("month_year", e.target.value)} placeholder="February 2026" />
                  </div>
                  <div>
                    <Label>Hero image URL (optional)</Label>
                    <Input value={compose.hero_image_url} onChange={(e) => setC("hero_image_url", e.target.value)} placeholder="https://…" />
                  </div>
                </div>
                <div>
                  <Label>Headline</Label>
                  <Input value={compose.headline} onChange={(e) => setC("headline", e.target.value)} placeholder="Show season warm-up" data-testid="compose-headline" />
                </div>
                <div>
                  <Label>Subhead (one line)</Label>
                  <Input value={compose.subhead} onChange={(e) => setC("subhead", e.target.value)} placeholder="What this issue is about" data-testid="compose-subhead" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-medium">From Karin&apos;s desk</h3>
                <Textarea rows={4} value={compose.intro_paragraph} onChange={(e) => setC("intro_paragraph", e.target.value)} placeholder="Opening paragraph…" />
                <Textarea rows={3} value={compose.intro_paragraph_2} onChange={(e) => setC("intro_paragraph_2", e.target.value)} placeholder="Optional second paragraph…" />
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-medium">Featured article (optional)</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input value={compose.article_section_label} onChange={(e) => setC("article_section_label", e.target.value)} placeholder="Section label — e.g. Training tip" />
                  <Input value={compose.article_title} onChange={(e) => setC("article_title", e.target.value)} placeholder="Article title" />
                </div>
                <Textarea rows={5} value={compose.article_body} onChange={(e) => setC("article_body", e.target.value)} placeholder="Article body…" />
                <div className="grid grid-cols-2 gap-3">
                  <Input value={compose.article_cta_label} onChange={(e) => setC("article_cta_label", e.target.value)} placeholder="Button label — Read more →" />
                  <Input value={compose.article_cta_url} onChange={(e) => setC("article_cta_url", e.target.value)} placeholder="https://…" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-medium">🎙️ Podcast spotlight (optional)</h3>
                <Input value={compose.podcast_episode_title} onChange={(e) => setC("podcast_episode_title", e.target.value)} placeholder="Episode title" />
                <div className="grid grid-cols-2 gap-3">
                  <Input value={compose.podcast_meta} onChange={(e) => setC("podcast_meta", e.target.value)} placeholder="Episode 42 · 38 min" />
                  <Input value={compose.podcast_url} onChange={(e) => setC("podcast_url", e.target.value)} placeholder="Episode URL (defaults to /podcast)" />
                </div>
                <Textarea rows={3} value={compose.podcast_teaser} onChange={(e) => setC("podcast_teaser", e.target.value)} placeholder="Episode teaser…" />
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-medium">🐩 Poodle Gang update (optional)</h3>
                <Input value={compose.gang_title} onChange={(e) => setC("gang_title", e.target.value)} placeholder="Section title" />
                <Textarea rows={4} value={compose.gang_body} onChange={(e) => setC("gang_body", e.target.value)} placeholder="What's happening with Liz, Grace, Alfred, Emma & Mary…" />
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-3">
                <h3 className="font-medium">📅 What&apos;s coming up (optional)</h3>
                {compose.upcoming_events.map((ev, i) => (
                  <div key={`evt-${i}`} className="flex gap-2">
                    <Input value={ev} onChange={(e) => setEvent(i, e.target.value)} placeholder={`e.g. Mar 14 — Bluegrass Classic, Lexington`} />
                    <button type="button" onClick={() => removeEventRow(i)} className="px-2 rounded text-muted-foreground hover:bg-destructive hover:text-destructive-foreground" aria-label="Remove event">
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={addEventRow} type="button">
                  <Plus size={14} className="mr-1.5" /> Add event row
                </Button>
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-medium">Closing</h3>
                <Textarea rows={3} value={compose.closing_line} onChange={(e) => setC("closing_line", e.target.value)} placeholder="Short closing line above the signature…" />
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-3">
                <h3 className="font-medium">Substack link (optional)</h3>
                <Label htmlFor="compose-substack-url" className="sr-only">Substack URL</Label>
                <Input
                  id="compose-substack-url"
                  value={compose.substack_url}
                  onChange={(e) => setC("substack_url", e.target.value)}
                  placeholder="https://dogsandhorses.substack.com/p/your-post-slug"
                  data-testid="compose-substack-url"
                />
                <p className="text-xs text-muted-foreground">
                  Adds a <strong>Read on Substack →</strong> button above the email footer so subscribers can read the post in your Substack archive. Leave blank to hide.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* ====== RIGHT: preview + actions ====== */}
          <div className="space-y-4 lg:sticky lg:top-20 lg:self-start">
            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">Preview</h3>
                  <Button variant="outline" size="sm" onClick={refreshPreview} type="button" data-testid="compose-preview-refresh">
                    <RefreshCw size={14} className="mr-1.5" /> Refresh
                  </Button>
                </div>
                <div className="border border-border rounded-md overflow-hidden bg-secondary/30" style={{ height: 560 }}>
                  {previewHtml ? (
                    <iframe
                      title="Newsletter preview"
                      data-testid="compose-preview-iframe"
                      srcDoc={previewHtml}
                      className="w-full h-full bg-white"
                      sandbox="allow-same-origin"
                    />
                  ) : (
                    <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground p-6">
                      Fill in the form and click <strong className="mx-1">Refresh</strong> to see the live preview.
                    </div>
                  )}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 pt-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => sendNewsletter(true)}
                    disabled={sending}
                    className="flex-1"
                    data-testid="compose-send-test"
                  >
                    <Send size={14} className="mr-1.5" /> {sending ? "Sending…" : "Send test to me"}
                  </Button>
                  <Button
                    type="button"
                    onClick={() => sendNewsletter(false)}
                    disabled={sending}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="compose-send-all"
                  >
                    <Send size={14} className="mr-1.5" /> {sending ? (scheduleMode ? "Scheduling…" : "Sending…") : (scheduleMode ? "Schedule send" : "Send to all subscribers")}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Test sends only to <strong>{user?.email}</strong>. Broadcast sends to every active subscriber.
                </p>

                {/* Verify Resend preflight — one-click self-diagnosis of
                    API key, domain verification, sender/reply-to sanity, and
                    subscriber headcount. Uses a plain modal (Dialog) rather
                    than an inline card so it doesn't push the send buttons
                    off-screen on mobile. */}
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={runPreflight}
                    className="text-xs w-full sm:w-auto"
                    data-testid="compose-preflight"
                  >
                    <ShieldCheck size={13} className="mr-1.5" /> Verify Resend before sending
                  </Button>
                </div>

                {/* Schedule-for-later — when toggled on the broadcast button
                    swaps to "Schedule send" and accepts a future datetime. The
                    backend queues the snapshot and dispatches it via a 60s
                    background loop. */}
                <div className="pt-3 mt-1 border-t border-border">
                  <label className="flex items-center gap-2 text-sm font-medium cursor-pointer select-none" data-testid="compose-schedule-toggle-label">
                    <input
                      type="checkbox"
                      checked={scheduleMode}
                      onChange={(e) => {
                        setScheduleMode(e.target.checked);
                        if (!e.target.checked) setC("scheduled_at", "");
                      }}
                      data-testid="compose-schedule-toggle"
                      className="h-4 w-4"
                    />
                    <Calendar size={14} className="text-muted-foreground" />
                    Schedule for later
                  </label>
                  {scheduleMode && (
                    <div className="mt-3 space-y-2">
                      <Label htmlFor="compose-scheduled-at" className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Send at (your local time)</Label>
                      <Input
                        id="compose-scheduled-at"
                        type="datetime-local"
                        value={compose.scheduled_at}
                        onChange={(e) => setC("scheduled_at", e.target.value)}
                        data-testid="compose-scheduled-at-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Stored as UTC. You can cancel any time before it fires from the <strong>Scheduled</strong> list below.
                      </p>
                    </div>
                  )}
                </div>

                {/* Substack-friendly exports — see _render_newsletter_markdown in
                    backend/routes/admin.py. The Markdown copy preserves
                    headings, links, lists and blockquotes when pasted into the
                    Substack editor. */}
                <div className="pt-3 mt-1 border-t border-border">
                  <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">Publish elsewhere</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={copyMarkdownForSubstack}
                      className="flex-1"
                      data-testid="compose-copy-markdown"
                    >
                      <Copy size={14} className="mr-1.5" /> Copy as Markdown
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={downloadMarkdownFile}
                      className="flex-1"
                      data-testid="compose-download-markdown"
                    >
                      <FileDown size={14} className="mr-1.5" /> Download .md
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Paste straight into Substack — headings, links, images and lists carry over cleanly.
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg" data-testid="scheduled-sends-card">
              <CardContent className="p-5">
                <h3 className="font-medium mb-3">Scheduled</h3>
                {scheduledSends.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nothing queued. Toggle <strong>Schedule for later</strong> above to queue a send.</p>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {scheduledSends.map((s) => (
                      <li key={s.id} className="py-2 flex items-center justify-between gap-3" data-testid={`scheduled-${s.id}`}>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{s.subject}</p>
                          <p className="text-xs text-muted-foreground">
                            Fires {s.scheduled_at ? fmt(s.scheduled_at) : "—"}
                          </p>
                        </div>
                        <button
                          onClick={() => cancelScheduled(s.id)}
                          className="text-xs text-destructive hover:underline shrink-0"
                          data-testid={`scheduled-cancel-${s.id}`}
                        >
                          Cancel
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card className="border-border shadow-none rounded-lg">
              <CardContent className="p-5">
                <h3 className="font-medium mb-3">Past sends</h3>
                {broadcasts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No newsletters sent yet.</p>
                ) : (
                  <ul className="divide-y divide-border text-sm">
                    {broadcasts.map((b) => (
                      <li key={b.id} className="py-2 flex items-center justify-between gap-3" data-testid={`broadcast-${b.id}`}>
                        <div className="min-w-0">
                          <p className="font-medium truncate">{b.subject}</p>
                          <p className="text-xs text-muted-foreground">
                            {b.sent_at ? fmt(b.sent_at) : "—"} · {b.sent_count} sent
                            {b.failed_count ? <span className="text-destructive"> · {b.failed_count} failed</span> : null}
                            {b.skipped_count ? <span className="text-amber-600"> · {b.skipped_count} skipped</span> : null}
                            {b.test_only ? <span className="ml-1 inline-block px-1.5 py-0.5 rounded bg-secondary text-xs">test</span> : null}
                          </p>
                          {/* Surface up to 3 dedup'd Resend error strings when a
                              send fails so the admin can triage without
                              opening the Resend dashboard. */}
                          {b.failure_reasons && b.failure_reasons.length > 0 ? (
                            <ul
                              className="mt-1 text-[11px] text-destructive/80 list-disc pl-4 leading-snug"
                              data-testid={`broadcast-failure-reasons-${b.id}`}
                            >
                              {b.failure_reasons.map((reason, i) => (
                                <li key={`${b.id}-fail-${i}`} className="break-words">{reason}</li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {tab === "contacts" && (
        <ContactsTab contacts={contacts} loading={loading} markRead={markRead} />
      )}

      {tab === "episodes" && <EpisodesTab />}

      {tab === "email-health" && (
        <EmailHealthTab
          emailHealth={emailHealth}
          recentOptouts={recentOptouts}
          loadEmailHealth={loadEmailHealth}
        />
      )}

      {/* Verify Resend preflight modal — surfaces API-key/domain/sender
          checks so the admin can self-diagnose before hitting Send. */}
      <PreflightDialog
        open={preflightOpen}
        onOpenChange={setPreflightOpen}
        busy={preflightBusy}
        result={preflightResult}
        onRerun={runPreflight}
      />

    </div>
  );
}
